/* eslint-disable prefer-const */
import Tooltip from '@material-ui/core/Tooltip';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import round from 'lodash/round';
import sumBy from 'lodash/sumBy';
import { FC, useEffect, useMemo } from 'react';
import {
  getDefaultDiagramTextColor,
  getDiagramUniversColorForRank,
} from '../../../../shared/utils/diagramUnivers';
import { useMinorResultHorizontalDiagramStyles } from './MinorResultHorizontalDiagram.styles';

type MinorResultHorizontalDiagramProps = {
  userResponse: any;
  variables: any[];
  diagramId: number;
  sendDataMax?: (data: any) => void;
};

const stripHtml = (html: string) => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html || '';
  return tmp.textContent || tmp.innerText || '';
};

// Conserver le repli historique du score plein lorsque l'éditeur définit une couleur.
const FULL_SCORE_BAR_COLORS = [
  '#FDDA5D',
  '#F9BC5D',
  '#F18965',
  '#D45E6B',
  '#BB3570',
  '#8B2D90',
  '#602faa',
  '#414197',
];

export const getMinorHorizontalBarColor = (
  diagramId: number,
  rank: number,
  codeColor: string | null | undefined,
  univers?: string | null
) => {
  if (diagramId === 5) {
    return (
      codeColor ||
      (univers
        ? FULL_SCORE_BAR_COLORS[rank % FULL_SCORE_BAR_COLORS.length]
        : getDiagramUniversColorForRank(univers, rank, 'darkToLight'))
    );
  }

  return getDiagramUniversColorForRank(univers, rank, 'darkToLight');
};

const MinorResultHorizontalDiagram: FC<MinorResultHorizontalDiagramProps> = ({
  userResponse,
  variables,
  diagramId,
  sendDataMax,
}) => {
  const classes = useMinorResultHorizontalDiagramStyles();
  const isRawScore = diagramId === 5;
  const univers = userResponse?.attributes?.test?.data?.attributes?.univers;

  let allVariables: any[] = [];
  userResponse?.attributes?.responses?.data.forEach((response: any) => {
    response?.attributes.variable.data.forEach((variable: any) => {
      allVariables.push({
        ...variable,
        variableValue: response.attributes.variableValue,
      });
    });
  });

  const variableGroup = groupBy(allVariables, (variable: any) => variable.id);

  const dataMapValue = map(variableGroup, (groupedVariables: any[]) => {
    const sumVariable = sumBy(groupedVariables, (variable: any) => variable.variableValue);
    const firstVariable = groupedVariables[0];

    return {
      value: sumVariable,
      label: stripHtml(firstVariable.attributes.title),
      title: firstVariable.attributes.title,
      image: firstVariable.attributes.image,
      video: firstVariable.attributes.video,
      typeMedia: firstVariable.attributes.typeMedia,
      description: firstVariable.attributes.description,
      variableId: firstVariable.id,
      codeColor: firstVariable.attributes.codeColor,
      textRestitutionChart: firstVariable.attributes.textRestitutionChart,
    };
  });

  const totalValue = sumBy(dataMapValue, (item: any) => item.value) || 0;
  const userVariablesIds = dataMapValue.map((d: any) => d.variableId);

  const sortedItems = useMemo(() => {
    const withValues = dataMapValue
      .sort((a: any, b: any) => b.value - a.value)
      .map((item: any, index: number) => {
        const displayValue = isRawScore
          ? item.value
          : round(totalValue ? (item.value * 100) / totalValue : 0, 2);

        return {
          ...item,
          value: displayValue,
          color: getMinorHorizontalBarColor(diagramId, index, item.codeColor, univers),
        };
      });

    const missingItems = variables
      .filter((v: any) => !userVariablesIds.includes(v.id))
      .map((mV: any, index: number) => ({
        value: 0,
        label: stripHtml(mV.attributes.title),
        title: mV.attributes.title,
        description: mV.attributes.description,
        image: '',
        video: '',
        typeMedia: '',
        textRestitutionChart: '',
        color: getMinorHorizontalBarColor(
          diagramId,
          withValues.length + index,
          mV.attributes?.codeColor,
          univers
        ),
      }));

    return withValues.concat(missingItems);
  }, [dataMapValue, totalValue, variables, userVariablesIds, isRawScore, univers, diagramId]);

  const maximumResponse = useMemo(() => sortedItems[0], [sortedItems]);
  const maxValue = Math.max(...sortedItems.map((item: any) => item.value), 1);

  useEffect(() => {
    if (sendDataMax && maximumResponse) {
      sendDataMax(maximumResponse);
    }
  }, [maximumResponse, sendDataMax]);

  return (
    <div className={classes.horizontalBars}>
      {sortedItems.map((item: any) => {
        const widthPercent = item.value > 0 ? (item.value / maxValue) * 100 : 0;
        const displayValue = isRawScore ? item.value : `${item.value}%`;

        return (
          <div key={item.label} className={classes.horizontalBarGroup}>
            <div className={classes.horizontalBarLabel}>{item.label}</div>
            <div className={classes.horizontalBarTrack}>
              <Tooltip title={item.textRestitutionChart || ''} placement="top">
                <div
                  className={classes.horizontalBarFill}
                  style={{
                    width: `${widthPercent}%`,
                    backgroundColor: item.value > 0 ? item.color : 'transparent',
                  }}>
                  <span
                    style={{
                      color:
                        item.value > 0
                          ? getDefaultDiagramTextColor(item.color, '#FFFFFF')
                          : undefined,
                    }}
                    className={`${classes.horizontalBarValue} ${
                      item.value === 0 ? classes.horizontalBarValueEmpty : ''
                    }`}>
                    {displayValue}
                  </span>
                </div>
              </Tooltip>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MinorResultHorizontalDiagram;
